import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Eye, EyeOff, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { ErroApi } from '../../api/cliente';
import { mensagens as apiMensagens } from '../../api/endpoints';
import type { Mensagem } from '../../api/tipos';
import { Aviso } from '../../components/Aviso';
import { Botao } from '../../components/Botao';
import { Campo } from '../../components/Campo';
import { Carregando, Pagina } from '../../components/Pagina';

const CHAVE = ['admin', 'mensagens'];

/** Rodapé do PDF dos representantes: incluir, editar, reordenar, ativar/inativar (SPEC §8.8). */
export function Mensagens() {
  const cliente = useQueryClient();
  const { data, isPending, error } = useQuery({ queryKey: CHAVE, queryFn: apiMensagens.listar });
  const [nova, setNova] = useState('');
  const [editando, setEditando] = useState<{ id: number; texto: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const aplicar = (r: { mensagens: Mensagem[] }) => {
    cliente.setQueryData(CHAVE, r);
    setErro(null);
  };
  const falhou = (e: unknown) => setErro(e instanceof ErroApi ? e.message : 'Não foi possível salvar.');

  const criar = useMutation({
    mutationFn: () => apiMensagens.criar(nova),
    onSuccess: (r) => {
      aplicar(r);
      setNova('');
    },
    onError: falhou,
  });
  const editar = useMutation({
    mutationFn: ({ id, dados }: { id: number; dados: { texto?: string; ativo?: boolean } }) => apiMensagens.editar(id, dados),
    onSuccess: (r) => {
      aplicar(r);
      setEditando(null);
    },
    onError: falhou,
  });
  const ordenar = useMutation({ mutationFn: apiMensagens.ordenar, onSuccess: aplicar, onError: falhou });

  function mover(indice: number, delta: number) {
    const lista = [...(data?.mensagens ?? [])];
    const [m] = lista.splice(indice, 1);
    lista.splice(indice + delta, 0, m!);
    ordenar.mutate(lista.map((x) => x.id));
  }

  return (
    <Pagina titulo="Mensagens do PDF" contexto="Condições impressas no rodapé do PDF dos representantes, nesta ordem. Mensagens inativas não aparecem.">
      <div className="flex max-w-3xl flex-col gap-4">
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        {isPending ? (
          <Carregando />
        ) : error ? (
          <Aviso tipo="erro">{error.message}</Aviso>
        ) : (
          <ol className="divide-y divide-linha-suave rounded-painel border border-linha bg-superficie">
            {data.mensagens.length === 0 && <li className="px-4 py-6 text-center text-tinta-2">Nenhuma mensagem cadastrada.</li>}
            {data.mensagens.map((m, i) => (
              <li key={m.id} className={clsx('flex items-center gap-2 px-3 py-2', !m.ativo && 'text-tinta-2')}>
                <span className="numero w-6 text-right text-[13px] text-tinta-2">{i + 1}.</span>
                {editando?.id === m.id ? (
                  <form
                    className="flex flex-1 items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      editar.mutate({ id: m.id, dados: { texto: editando.texto } });
                    }}
                  >
                    <input
                      autoFocus
                      aria-label="Texto da mensagem"
                      value={editando.texto}
                      maxLength={255}
                      onChange={(e) => setEditando({ id: m.id, texto: e.target.value })}
                      onKeyDown={(e) => e.key === 'Escape' && setEditando(null)}
                      className="h-8 flex-1 rounded-campo border border-linha px-2"
                    />
                    <Botao type="submit" tamanho="compacto" variante="principal" carregando={editar.isPending}>
                      Salvar
                    </Botao>
                    <Botao tamanho="compacto" variante="discreto" onClick={() => setEditando(null)}>
                      Cancelar
                    </Botao>
                  </form>
                ) : (
                  <>
                    <span className={clsx('flex-1', !m.ativo && 'line-through')}>{m.texto}</span>
                    {!m.ativo && <span className="text-[12px]">inativa</span>}
                    <div className="flex gap-0.5">
                      <Botao tamanho="compacto" variante="discreto" aria-label="Subir" disabled={i === 0 || ordenar.isPending} onClick={() => mover(i, -1)}>
                        <ArrowUp aria-hidden className="size-3.5" />
                      </Botao>
                      <Botao tamanho="compacto" variante="discreto" aria-label="Descer" disabled={i === data.mensagens.length - 1 || ordenar.isPending} onClick={() => mover(i, 1)}>
                        <ArrowDown aria-hidden className="size-3.5" />
                      </Botao>
                      <Botao tamanho="compacto" variante="discreto" aria-label="Editar" onClick={() => setEditando({ id: m.id, texto: m.texto })}>
                        <Pencil aria-hidden className="size-3.5" />
                      </Botao>
                      <Botao tamanho="compacto" variante="discreto" onClick={() => editar.mutate({ id: m.id, dados: { ativo: !m.ativo } })}>
                        {m.ativo ? <EyeOff aria-hidden className="size-3.5" /> : <Eye aria-hidden className="size-3.5" />}
                        {m.ativo ? 'Inativar' : 'Ativar'}
                      </Botao>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ol>
        )}
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (nova.trim()) criar.mutate();
          }}
        >
          <Campo rotulo="Nova mensagem" value={nova} maxLength={255} onChange={(e) => setNova(e.target.value)} className="flex-1" />
          <Botao type="submit" variante="secundario" carregando={criar.isPending} disabled={!nova.trim()} className="h-9">
            <Plus aria-hidden className="size-4" /> Incluir
          </Botao>
        </form>
      </div>
    </Pagina>
  );
}
